import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, ArrowRight, Navigation } from "lucide-react";
import { useLang, API } from "@/context/LanguageContext";
import { useSEO } from "@/components/seo";
import { Reveal, SectionHead } from "@/components/motion";

const MAPS_QUERY = encodeURIComponent("Pflerscherstraße 54, 39041 Colle Isarco BZ, Italien");

export default function Contact() {
  const { t, lang, pathFor } = useLang();
  useSEO("contact");
  const c = t.contactPage;
  const [mapLoaded, setMapLoaded] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", message: "", website: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(c.formSuccess);
      setForm({ name: "", email: "", message: "", website: "" });
    } catch {
      toast.error(c.formError);
    } finally {
      setSending(false);
    }
  };

  const rows = [
    { icon: Phone, label: c.labels.phone, value: "+39 345 580 7242", href: "tel:+393455807242", testid: "contact-phone" },
    { icon: Mail, label: c.labels.email, value: "info@r-mechanik.it", href: "mailto:info@r-mechanik.it", testid: "contact-email" },
    { label: c.labels.vat, value: "IT03286770213", testid: "contact-vat" },
    { label: c.labels.pec, value: "r-mechanik@legalmail.it", href: "mailto:r-mechanik@legalmail.it", testid: "contact-pec" },
    { label: c.labels.sdi, value: "USAL8PV", testid: "contact-sdi" },
  ];

  return (
    <div className="bg-anthracite-dark pt-16" data-testid="contact-page">
      <section className="border-b border-white/10 bg-anthracite grid-lines-dark">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:py-28">
          <SectionHead kicker={c.kicker} title={c.title} light testid="contact" />
          <Reveal delay={0.1}><p className="mt-6 max-w-2xl text-slate-400">{c.intro}</p></Reveal>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-8 lg:grid-cols-2">
        <div>
          <Reveal>
            <div className="border border-white/10 bg-anthracite p-8" data-testid="contact-company-card">
              <h2 className="text-xl font-black uppercase tracking-tight text-white">R-Mechanik SRLS</h2>
              <p className="mt-1 text-sm text-slate-400">{c.labels.owner}: Julian Rizzi</p>
              <div className="mt-6 flex items-start gap-3 text-sm text-slate-300">
                <MapPin size={16} className="mt-0.5 shrink-0 text-royal-soft" />
                <span>Pflerscherstraße 54<br />39041 Gossensaß / Colle Isarco (BZ)<br />Südtirol – Italien</span>
              </div>
              <dl className="mt-6 divide-y divide-white/10 border-y border-white/10">
                {rows.map((r) => (
                  <div key={r.testid} className="flex items-center justify-between gap-4 py-3 text-sm">
                    <dt className="flex items-center gap-2 text-slate-400">
                      {r.icon && <r.icon size={14} className="text-royal-soft" />}
                      {r.label}
                    </dt>
                    <dd className={r.value === c.placeholder ? "font-mono-tech text-xs text-amber-300/80" : "text-white"} data-testid={r.testid}>
                      {r.href ? <a href={r.href} className="text-royal-soft hover:text-white">{r.value}</a> : r.value}
                    </dd>
                  </div>
                ))}
                <div className="flex items-center justify-between gap-4 py-3 text-sm">
                  <dt className="flex items-center gap-2 text-slate-400"><Clock size={14} className="text-royal-soft" />{c.labels.hours}</dt>
                  <dd className="text-right text-white" data-testid="contact-hours">{c.hoursDays}<br />{c.hoursTimes.join(" · ")}</dd>
                </div>
              </dl>
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${MAPS_QUERY}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group mt-6 inline-flex items-center gap-3 border border-white/20 px-6 py-3 text-sm font-semibold text-white transition-colors hover:border-royal-soft hover:text-royal-soft"
                data-testid="route-planner-button"
              >
                <Navigation size={16} />
                {c.routeButton}
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-8 border border-white/10 bg-anthracite" data-testid="map-container">
              <div className="border-b border-white/10 px-6 py-4">
                <h3 className="font-mono-tech text-xs uppercase tracking-[0.3em] text-royal-soft">{c.mapTitle}</h3>
              </div>
              {mapLoaded ? (
                <iframe
                  title="Standort R-Mechanik SRLS"
                  src={`https://www.google.com/maps?q=${MAPS_QUERY}&output=embed&hl=${lang}`}
                  className="h-72 w-full border-0"
                  loading="lazy"
                  data-testid="map-iframe"
                />
              ) : (
                <div className="flex h-72 flex-col items-center justify-center gap-4 px-8 text-center">
                  <MapPin size={28} className="text-royal-soft" />
                  <p className="max-w-sm text-xs leading-relaxed text-slate-400">{c.mapText}</p>
                  <button onClick={() => setMapLoaded(true)} className="border border-royal px-5 py-2.5 text-sm font-semibold text-royal-soft transition-colors hover:bg-royal hover:text-white" data-testid="map-load-button">
                    {c.mapLoad}
                  </button>
                </div>
              )}
            </div>
          </Reveal>
        </div>

        <div>
          <Reveal delay={0.05}>
            <form onSubmit={submit} className="border border-white/10 bg-anthracite p-8" data-testid="contact-form">
              <h2 className="text-xl font-black uppercase tracking-tight text-white">{c.formTitle}</h2>
              <div className="mt-6 space-y-5">
                <div>
                  <label htmlFor="contact-name" className="mb-2 block font-mono-tech text-xs uppercase tracking-widest text-slate-400">{c.formName}</label>
                  <input id="contact-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full border border-white/15 bg-anthracite-dark px-4 py-3 text-sm text-white outline-none transition-colors focus:border-royal" data-testid="contact-form-name" />
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-2 block font-mono-tech text-xs uppercase tracking-widest text-slate-400">{c.formEmail}</label>
                  <input id="contact-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full border border-white/15 bg-anthracite-dark px-4 py-3 text-sm text-white outline-none transition-colors focus:border-royal" data-testid="contact-form-email" />
                </div>
                <div>
                  <label htmlFor="contact-message" className="mb-2 block font-mono-tech text-xs uppercase tracking-widest text-slate-400">{c.formMessage}</label>
                  <textarea id="contact-message" required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full resize-none border border-white/15 bg-anthracite-dark px-4 py-3 text-sm text-white outline-none transition-colors focus:border-royal" data-testid="contact-form-message" />
                </div>
                <input type="text" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                <button type="submit" disabled={sending} className="w-full bg-royal px-6 py-4 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-royal-hover disabled:opacity-50" data-testid="contact-form-submit">
                  {sending ? c.formSending : c.formSend}
                </button>
              </div>
            </form>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="mt-8 flex flex-col items-start justify-between gap-6 border border-royal/40 bg-royal/10 p-8 sm:flex-row sm:items-center" data-testid="contact-inquiry-cta">
              <div>
                <h3 className="font-bold text-white">{c.inquiryCtaTitle}</h3>
                <p className="mt-2 text-sm text-slate-400">{c.inquiryCtaText}</p>
              </div>
              <Link to={pathFor("inquiry")} className="group flex shrink-0 items-center gap-2 bg-royal px-6 py-3.5 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-royal-hover" data-testid="contact-to-inquiry-button">
                {c.inquiryCtaButton}
                <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
